import { ref } from 'vue';

import { API_ERRORS } from '@/constants/api';

export default function useApi(options = { useVersion: true }) {
  const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://localhost:3000';
  const version = 1;
  const getXsrfToken = () => {
    const cookies = document.cookie.split(';');
    const xsrfCookie = cookies.find((cookie) => cookie.trim().startsWith('XSRF-TOKEN='));
    return xsrfCookie ? decodeURIComponent(xsrfCookie.split('=')[1]) : null;
  };

  const request = async (endpoint, options = {}) => {
    const baseUrlTrimmed = baseUrl.replace(/^\/|\/$/g, '');
    const endpointTrimmed = endpoint.replace(/^\/|\/$/g, '');
    const trimUrl = options.useVersion
      ? `${baseUrlTrimmed}/v${version}/${endpointTrimmed}`
      : `${baseUrlTrimmed}/${endpointTrimmed}`;
    const loading = ref(false);
    loading.value = true;

    try {
      const defaultHeaders = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-XSRF-TOKEN': getXsrfToken(),
      };

      const response = await fetch(`${trimUrl}`, {
        ...options,
        credentials: 'include',
        mode: 'cors',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      });

      const data = await response.json().catch(() => null);

      // the session behind the token expired (e.g. a tab left open for hours): take a fresh token and retry once
      if (response.status === 419 && !options.isCsrfRetry) {
        await fetch(`${baseUrlTrimmed}/sanctum/csrf-cookie`, {
          credentials: 'include',
          mode: 'cors',
        });
        return request(endpoint, { ...options, isCsrfRetry: true });
      }

      if (response.status === 419) {
        return { data: null, error: API_ERRORS.csrfMismatch, status: response.status, loading };
      }

      if (response.status === 429) {
        return { data: null, error: API_ERRORS.tooManyAttempts, status: response.status, loading };
      }

      if (!response.ok) {
        return {
          data: null,
          error: data?.message || `API request failed with status ${response.status}`,
          status: response.status,
          loading,
        };
      }

      return { data, error: null, loading };
    } catch (err) {
      return {
        data: null,
        error: err.message || 'Network error occurred',
        loading,
      };
    } finally {
      loading.value = false;
    }
  };

  const get = (endpoint, requestOptions = {}) => {
    return request(endpoint, { ...requestOptions, method: 'GET', useVersion: options.useVersion });
  };

  const post = (endpoint, data, requestOptions = {}) => {
    return request(endpoint, {
      ...requestOptions,
      method: 'POST',
      body: JSON.stringify(data),
      useVersion: options.useVersion,
    });
  };

  const put = (endpoint, data, requestOptions = {}) => {
    return request(endpoint, {
      ...requestOptions,
      method: 'PUT',
      body: JSON.stringify(data),
      useVersion: options.useVersion,
    });
  };

  const remove = (endpoint, requestOptions = {}) => {
    return request(endpoint, {
      ...requestOptions,
      method: 'DELETE',
      useVersion: options.useVersion,
    });
  };

  const patch = (endpoint, data, requestOptions = {}) => {
    return request(endpoint, {
      ...requestOptions,
      method: 'PATCH',
      body: JSON.stringify(data),
      useVersion: options.useVersion,
    });
  };

  return {
    get,
    post,
    put,
    remove,
    patch,
  };
}
